import { DataCleaningTypeGuardService } from "./DataCleaningTypeGuardService";
import { Rule } from "../models/Rule";
import { RuleSchema } from "../models/RuleSchema";
import { RuleField } from "../models/RuleField";
import { RuleFieldType } from "../models/RuleFieldType";
import { QueueDataCleaningArgs } from "@/shared/Executions/models/QueueDataCleaningArgs";
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { IQueueExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { QueueExecutionRequestFactory } from "@/shared/Executions/services/QueueExecutionRequestFactory";

export class DataCleaningService {

    public static readonly CUSTOM_RULE_TYPE = "unknown";

    /// Rules have abitrary keys as their typing is relatively loose.
    /// This method will reduce/flatten the output to include only what is expected
    /// in rule processing.
    public static getFlattenedRule(rule: Rule, includeType: string | null): Rule {
        if (rule == null) throw new Error("rule is required");

        if (rule.schema?.payload_template != null) {
            const payload_template = rule.schema.payload_template;
            return DataCleaningService.hydratePayloadTemplate(payload_template, rule.configuration || {});
        }
        else {
            const ruleObject = rule.configuration ?? rule;
            const { _type, ...properties } = ruleObject || {};
            const flattenedRule = { ...properties } as Rule;
            // Only include type data if at the top-level or configuration is a a known rule type
            if (includeType) {
                flattenedRule.type = includeType
            }
            return flattenedRule;
        }

    }

    public static createRuleFromRuleSchema(ruleSchema: RuleSchema): Rule {

        const newRule: Rule = {
            id: this.generateRuleId(),
            _type: ruleSchema.type,
            display_name: ruleSchema.display_name,
            description: 'New rule configuration',
            category: ruleSchema.category,
            configuration: {},
            schema: ruleSchema
        }

        return newRule;

    }

    public static createRuleFromRuleField(ruleField: RuleField): Rule {

        const newRule: Rule = {
            id: this.generateRuleId(),
            _type: ruleField.field_type,
            display_name: ruleField.display_name,
            display_field: ruleField.display_field,
            description: ruleField.description,
            category: "Default Value",
            configuration: {},
            schema: ruleField
        }

        return newRule;

    }

    private static generateRuleId(): string {
        const id = `rule-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        return id;
    }

    public static initializeRuleConfiguration(rule: Rule, fields: Array<RuleField>): Record<string, any> {
        const config: Record<string, any> = {}
        config.description = `${rule.display_name ?? "Rule"} Description`
        fields.forEach((field: RuleField) => {
            this.initializeRuleConfigurationForField(field, config);
        })
        config._type = rule._type
        return config;
    }

    public static initializeRuleFieldConfiguration(fields: Array<RuleField>): Record<string, any> {
        const config: Record<string, any> = {}
        //config.description = `${rule.display_name ?? "Rule"} Description`
        fields.forEach((field: RuleField) => {
            this.initializeRuleConfigurationForField(field, config);
        })
        return config;
    }

    public static initializeRuleConfigurationForField(field: RuleField, configuration?: Record<string, any>): Record<string, any> {
        const config: Record<string, any> = configuration || {};
        if (field.default_value !== undefined) {
            config[field.name] = field.default_value;
        } else if (field.field_type === RuleFieldType.Boolean) {
            config[field.name] = false;
        } else if (
            field.field_type === RuleFieldType.MultiSelect ||
            field.field_type === RuleFieldType.ColumnMultiSelect ||
            field.field_type === RuleFieldType.SourceDestinationPairArray ||
            field.field_type === RuleFieldType.Array ||
            field.field_type === RuleFieldType.ColumnMultiSelectOrCreate
        ) {
            config[field.name] = [];
        } else if (
            field.field_type === RuleFieldType.Object ||
            field.field_type === RuleFieldType.StringTemplate
        ) {
            config[field.name] = {};
        }
        else {
            config[field.name] = '';
        }
        return config;
    }

    public static hydratePayloadTemplate(
        template: any,
        context: Record<string, any>
    ): any {

        if (typeof template === 'string') {
            const match = template.match(/^<([^<>]+)>$/);
            if (match) {
                const key = match[1];
                return context.hasOwnProperty(key) ? context[key] : template;
            }
            return template;
        }

        if (Array.isArray(template)) {
            return template.map(item => DataCleaningService.hydratePayloadTemplate(item, context));
        }

        if (template && typeof template === 'object') {
            const result: any = {};
            for (const [key, value] of Object.entries(template)) {
                result[key] = DataCleaningService.hydratePayloadTemplate(value, context);
            }
            return result;
        }

        return template; // return as-is for primitives (number, boolean, null, etc.)
    }

    public static scrubOutputFileName(name: string): string {

        // Until API works with no extension added, include temporarily

        //@ts-ignore
        const supportedExtensions = ['csv', 'dat', 'xls', 'xlsx', 'sas7bdat', 'xpt']
        /*
        for (const ext of supportedExtensions) {
            const suffix = `.${ext}`
            if (name.toLowerCase().endsWith(suffix)) {
                return name.slice(0, -suffix.length)
            }
        }*/

        return name
    }

    public static createQueueDataCleaningArgs(
        fileId: string,
        flattenedRules: Array<Rule>,
        outputFileName: string): QueueDataCleaningArgs {

        const args = new QueueDataCleaningArgs(
            flattenedRules,
            fileId
        )

        if (outputFileName != null) {
            const scrubbedFileName = DataCleaningService.scrubOutputFileName(outputFileName)
            if (scrubbedFileName?.length) {
                args.outputFileName = scrubbedFileName
            }
        }

        return args
    }

    public static getFormattedRulesJson(fileId: string, rules: Array<Rule>, outputFileName: string): IQueueExecutionRequest {

        const flattenedRules = this.getFlattendRules(rules);

        const args = DataCleaningService.createQueueDataCleaningArgs(fileId, flattenedRules, outputFileName);
        const queueExecutionArgs = new QueueExecutionArgs({ tenantId: '', userId: '', dataCleaningArgs: args });
        const payload = QueueExecutionRequestFactory.createQueueExecutionRequest(queueExecutionArgs);
        return payload;
    }

    public static getFlattendRules(rules: Array<Rule>): Array<Rule> {

        const flattenedRules = (rules || []).map((x) => DataCleaningService.getFlattenedRule(x, x._type));
        this.recursivelyFlattenRules(flattenedRules);
        return flattenedRules;

    }

    private static recursivelyFlattenRules(rules: Array<Rule | Object>, visited = new Set<any>()) {
        for (const rule of rules) {
            if (visited.has(rule)) continue;
            visited.add(rule);

            for (const key of Object.keys(rule)) {
                const value = (rule as any)[key];

                if (Array.isArray(value)) {
                    const containsSubRules =
                        value.length > 0 &&
                        value.every((item) => item && DataCleaningTypeGuardService.isRule(item));

                    if (containsSubRules) {
                        const flattenedSubRules = value.map((subRule: Rule) => {
                            const includeType = !this.isInternalSchemaType(subRule._type) ? subRule._type : null;
                            return this.getFlattenedRule(subRule, includeType);
                        });

                        (rule as any)[key] = flattenedSubRules;
                        this.recursivelyFlattenRules(flattenedSubRules, visited);
                    }
                }
                else if (DataCleaningTypeGuardService.isNonEmptyRuleObject(value)) {

                    for (const nestedKey of Object.keys(value)) {
                        const nestedValue = value[nestedKey];

                        // Case 1: Direct rule object
                        if (DataCleaningTypeGuardService.isRule(nestedValue)) {
                            const includeType = !this.isInternalSchemaType(nestedValue._type) ? nestedValue._type : null;
                            const flattened = this.getFlattenedRule(nestedValue, includeType);
                            value[nestedKey] = flattened;

                            this.recursivelyFlattenRules([flattened], visited);
                        }

                        // Case 2: Another nested non-empty rule-like object
                        else if (DataCleaningTypeGuardService.isNonEmptyRuleObject(nestedValue)) {
                            const coerced = nestedValue as Object;
                            this.recursivelyFlattenRules([coerced], visited);
                        }

                        else if (Array.isArray(nestedValue)) {
                            const rulesArr = this.extractRulesFromArray(nestedValue);
                            if (rulesArr.length > 0) {
                                const flattened = rulesArr.map(subRule => {
                                    const includeType = !this.isInternalSchemaType(subRule._type) ? subRule._type : null;
                                    return this.getFlattenedRule(subRule, includeType);
                                });
                                value[nestedKey] = flattened;
                                this.recursivelyFlattenRules(flattened, visited);
                            }
                        }
                    }
                }

            }
        }
    }

    private static extractRulesFromArray(arr: unknown[]): Rule[] {
        return arr.filter(DataCleaningTypeGuardService.isRule);
    }

    public static getFormattedRulesJsonString(file_id: string, rules: Array<Rule>, outputFileName: string, rulesOnly: boolean = false): string {
        const payload = DataCleaningService.getFormattedRulesJson(file_id, rules, outputFileName)

        return JSON.stringify((rulesOnly ? payload.dataCleaning.rules : payload), null, 2)
    }

    public static tryGetRuleSchemaByType(type: string, rulesSchemas: Array<RuleSchema>): RuleSchema | undefined {

        if (type == null) throw new Error("type is required");
        if (rulesSchemas == null) throw new Error("ruleSchemas are required");

        return rulesSchemas.find(x => x.type === type) || rulesSchemas.find(x => x.type === DataCleaningService.CUSTOM_RULE_TYPE);

    }

    // Certain rule item_scheam structures use types like 'object' or 'array' that we do not want
    // persisted in output JSON.
    public static isInternalSchemaType(type: string): boolean {

        const internalTypes = ['object', 'array'];
        return internalTypes.indexOf(type?.toLowerCase().trim()) !== -1;

    }


}


